import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ChartBarIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  CloudIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
// Logo SVG component removed — using anchor-logo.png for consistency with login page

const navItems = [
  { to: '/dashboard', label: 'Posture', icon: HomeIcon },
  { to: '/overview', label: 'Overview', icon: ChartBarIcon },
  { to: '/agents', label: 'Fleet', icon: CpuChipIcon },
  { to: '/audit', label: 'Assess', icon: ShieldCheckIcon },
  { to: '/cloud-backups', label: 'Cloud Backups', icon: CloudIcon },
  { to: '/reports', label: 'Reports', icon: DocumentChartBarIcon },
  { to: '/settings', label: 'Settings', icon: Cog6ToothIcon },
];

const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-64 bg-[rgba(10,9,32,0.85)] backdrop-blur-sm border-r border-white/[0.06] flex flex-col font-nippo z-30">
      {/* Logo section */}
      <div className="px-6 py-6 flex items-center space-x-3">
        <img src="/anchor-logo.png" alt="Data Anchor" className="w-8 h-8 object-contain" style={{ filter: 'drop-shadow(0 0 8px rgba(69,69,255,0.3))' }} />
        <span className="text-lg font-light text-white tracking-wide">DATA ANCHOR</span>
      </div>

      {/* Divider */}
      <div className="mx-4 border-t border-white/[0.06]" />

      {/* Navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1">
        {navItems.map(({ to, label, icon: Icon }) => {
          const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
          return (
            <NavLink
              key={to}
              to={to}
              className={`
                relative flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150
                ${isActive
                  ? 'text-anchor-blue bg-anchor-blue/[0.08]'
                  : 'text-anchor-muted hover:text-white hover:bg-white/[0.04]'
                }
              `}
            >
              {/* Active indicator bar */}
              {isActive && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-anchor-blue rounded-r-full" />
              )}
              <Icon className="h-5 w-5 mr-3 flex-shrink-0" />
              {label}
            </NavLink>
          );
        })}
      </nav>

      {/* Divider */}
      <div className="mx-4 border-t border-white/[0.06]" />

      {/* User section */}
      <div className="px-4 py-4">
        <div className="flex items-center space-x-3 mb-3">
          <div className="h-8 w-8 bg-anchor-blue/20 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-medium text-anchor-blue">
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-white truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-anchor-muted truncate">{user?.email || ''}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center px-3 py-2 text-sm text-anchor-muted hover:text-white hover:bg-white/[0.04] rounded-lg transition-colors duration-150"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
          Sign Out
        </button>
        <p className="px-3 pt-2 text-[10px] text-white/25" title={`Built ${__BUILD_TIME__}`}>
          build {__BUILD_SHA__}
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
